import React, { Component } from "react";
import { t } from "../../Helper/i18n";

/** Builds a self-contained HTML page (three.js + Rhino3dmLoader loaded as ES modules from a
 *  CDN via an import map) that fetches and renders a .3dm file with mouse-orbit/zoom. Rendered
 *  inside a sandboxed iframe via srcDoc so three.js/rhino3dm never touch the host page's globals
 *  -- the same self-contained-HTML approach used for the desktop app's webview embed, so both
 *  stay in sync by construction. rhino3dm@7.15.0 is pinned because it's the exact version
 *  three.js's own 3DMLoader example is built against; newer rhino3dm releases changed the
 *  instanceDefinitions() API shape and fail to parse. */
function buildViewerHtml(url) {
  const escapedUrl = JSON.stringify(url);
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<style>
  html,body{margin:0;padding:0;background:#0b1633;overflow:hidden;height:100%;}
  #viewer{width:100vw;height:100vh;}
  #status{position:absolute;top:0;left:0;right:0;color:#fff;font-family:Tahoma,Arial,sans-serif;font-size:13px;text-align:center;padding:10px;pointer-events:none;z-index:10;}
  #hint{position:absolute;bottom:8px;left:0;right:0;color:rgba(255,255,255,.55);font-family:Tahoma,Arial,sans-serif;font-size:11px;text-align:center;pointer-events:none;}
</style>
</head>
<body>
<div id="status">${t("model3dLoading")}</div>
<div id="viewer"></div>
<div id="hint">${t("model3dHint")}</div>
<script type="importmap">
{"imports":{"three":"https://unpkg.com/three@0.150.1/build/three.module.js","three/addons/":"https://unpkg.com/three@0.150.1/examples/jsm/"}}
</script>
<script type="module">
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { Rhino3dmLoader } from "three/addons/loaders/3DMLoader.js";

const statusEl = document.getElementById('status');
const hintEl = document.getElementById('hint');
const container = document.getElementById('viewer');

function viewSize() {
  return { w: window.innerWidth, h: window.innerHeight };
}

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x0b1633);

const v0 = viewSize();
const camera = new THREE.PerspectiveCamera(45, v0.w / v0.h, 0.01, 1000000);
camera.position.set(50, 50, 50);

const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setSize(v0.w, v0.h);
container.appendChild(renderer.domElement);

const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;

scene.add(new THREE.AmbientLight(0xffffff, 0.7));
const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
dirLight.position.set(1, 1, 1);
scene.add(dirLight);
const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
dirLight2.position.set(-1, -1, -1);
scene.add(dirLight2);

window.addEventListener('resize', () => {
  const v = viewSize();
  camera.aspect = v.w / v.h;
  camera.updateProjectionMatrix();
  renderer.setSize(v.w, v.h);
});

const loader = new Rhino3dmLoader();
loader.setLibraryPath("https://cdn.jsdelivr.net/npm/rhino3dm@7.15.0/");

loader.load(${escapedUrl}, function (object) {
  object.traverse((o) => {
    if (o.isMesh) {
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach((m) => { if (m) m.side = THREE.DoubleSide; });
    }
  });
  scene.add(object);

  const box = new THREE.Box3().setFromObject(object);
  const size = box.getSize(new THREE.Vector3());
  const radius = size.length() / 2 || 50;
  const center = box.getCenter(new THREE.Vector3());
  const dist = radius * 2.8;
  const dir = new THREE.Vector3(1, 0.8, 1).normalize();
  camera.position.copy(center).addScaledVector(dir, dist);
  camera.near = Math.max(radius / 1000, 0.001);
  camera.far = radius * 1000;
  camera.updateProjectionMatrix();
  controls.target.copy(center);
  camera.lookAt(center);
  controls.update();

  statusEl.style.display = 'none';
}, function (progress) {
  if (progress.total) {
    statusEl.textContent = 'جاري التحميل... ' + Math.round((progress.loaded / progress.total) * 100) + '%';
  }
}, function (error) {
  statusEl.textContent = 'تعذر تحميل النموذج ثلاثي الأبعاد';
  hintEl.style.display = 'none';
  console.error(error);
});

function animate() {
  requestAnimationFrame(animate);
  controls.update();
  renderer.render(scene, camera);
}
animate();
</script>
</body>
</html>`;
}

class Model3DViewer extends Component {
  render() {
    const { src } = this.props;
    return (
      <iframe
        className="model3d-frame"
        title={t("model3dTitle")}
        srcDoc={buildViewerHtml(src)}
        sandbox="allow-scripts allow-same-origin"
      />
    );
  }
}

export default Model3DViewer;
